/**
 * Where the back button on Explore Coverage and the Funding plan goes, and what it says.
 *
 * The clients dashboard opens these screens through `withReturnToClients`, which adds
 * `?from=clients`; their back button then reads "Back to Clients" and returns to the dashboard.
 * Without it the screen was opened from the client's own pages, so it returns to the fallback the
 * caller names (the Financing plan, or a consumer's Summary).
 *
 * The origin rides in the query string rather than router state because both screens rewrite their
 * own URL as they are used (quote filters and scroll position, wizard steps). Those rewrites keep
 * the query string but drop router state, and the query string also survives a reload.
 */
import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAdvisorNavigation } from '@/hooks/useAdvisorNavigation';

const FROM_PARAM = 'from';
const FROM_CLIENTS = 'clients';

export function withReturnToClients(path: string): string {
  return `${path}?${FROM_PARAM}=${FROM_CLIENTS}`;
}

interface BackDestination {
  label: string;
  path: string;
}

export function useBackDestination(fallback: BackDestination) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { navigateToClientsDashboard } = useAdvisorNavigation();
  const isFromClients = searchParams.get(FROM_PARAM) === FROM_CLIENTS;
  const fallbackPath = fallback.path;

  const goBack = useCallback(() => {
    if (isFromClients) {
      navigateToClientsDashboard();
    } else {
      navigate(fallbackPath);
    }
  }, [isFromClients, navigateToClientsDashboard, navigate, fallbackPath]);

  return {
    label: isFromClients ? 'Back to Clients' : fallback.label,
    goBack,
  };
}
